import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Eye,
  EyeOff,
  Loader2,
  Shield,
  ShoppingCart,
  Package,
  ArrowRight,
  CheckCircle,
  FileText,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { B2BonixLogo } from '@/components/B2BonixLogo';

const features = [
  { icon: ShoppingCart, text: 'Online sipariş ve hızlı teslimat' },
  { icon: Package, text: 'Stok durumu ve barkod sorgulama' },
  { icon: FileText, text: 'Cari hesap ve ödeme takibi' },
  { icon: Shield, text: 'Güvenli altyapı, KVKK uyumlu' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    const result = await login(email, password);
    if (result.success) {
      toast.success('Giriş başarılı!');
      const user = useAuthStore.getState().user;
      if (user?.role === 'super_admin' || user?.role === 'personnel') {
        navigate('/admin');
      } else if (user?.role === 'dealer') {
        navigate('/dealer');
      } else {
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #283086 0%, #1a224f 55%, #12173a 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#10b981]/10 blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-white/5 blur-[100px] translate-y-1/3" />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="login-logo-link">
            <B2BonixLogo variant="light" iconSize={44} asLink={false} />
          </Link>

          {/* Main content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
                Bayi siparişlerinizi
                <br />
                <span className="text-[#10b981]">kolayca yönetin.</span>
              </h1>
              <p className="mt-4 text-base text-white/70 leading-relaxed max-w-md">
                B2Bonix B2B Bayi Paneli ile ürün siparişi verin, cari hesabınızı takip edin, ödemelerinizi yönetin.
              </p>
            </div>

            <div className="space-y-3">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-sm text-white/80"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#10b981]">
                    <f.icon className="h-4 w-4" />
                  </div>
                  {f.text}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom info */}
          <div className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-sm p-5">
            <p className="text-sm text-white/80 leading-relaxed">
              İşletmenize özel B2B bayi yönetim platformu.
            </p>
            <p className="mt-2 text-xs text-white/50">&copy; {new Date().getFullYear()} B2Bonix by WebPlace. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-2">
            <B2BonixLogo variant="dark" iconSize={40} asLink={false} />
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Bayi Girişi
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              E-posta ve şifreniz ile giriş yapın
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">E-posta</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@firma.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Şifre</Label>
                <a
                  href="https://wa.me/902166069924?text=Merhaba%2C%20%C5%9Fifremi%20unuttum%2C%20%C5%9Fifre%20s%C4%B1f%C4%B1rlama%20talep%20ediyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#1a224f] hover:underline flex items-center gap-1"
                  data-testid="forgot-password-link"
                >
                  <MessageCircle className="h-3 w-3" />
                  Şifremi unuttum
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                  data-testid="password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                <p className="text-sm text-destructive" data-testid="error-message">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold bg-[#1a224f] hover:bg-[#12173a]"
              disabled={isLoading}
              data-testid="login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  Giriş Yap
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          {/* Demo accounts */}
          <div className="pt-2" data-testid="demo-section">
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-[11px]"><span className="bg-background px-3 text-muted-foreground">Demo Görüntüle</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { clearError(); setEmail('admin@webplace.tr'); setPassword('Admin123!'); }}
                className="flex flex-col items-center gap-0.5 rounded-lg border border-border bg-card px-3 py-2.5 text-xs font-medium transition-colors hover:border-[#1a224f] hover:bg-[#1a224f]/5"
                data-testid="demo-admin-btn"
              >
                <span className="text-[#1a224f] font-semibold">Admin Demo</span>
                <span className="text-[10px] text-muted-foreground">admin@webplace.tr</span>
              </button>
              <button
                type="button"
                onClick={() => { clearError(); setEmail('bayi@webplace.tr'); setPassword('Bayi123!'); }}
                className="flex flex-col items-center gap-0.5 rounded-lg border border-border bg-card px-3 py-2.5 text-xs font-medium transition-colors hover:border-[#10b981] hover:bg-[#10b981]/5"
                data-testid="demo-dealer-btn"
              >
                <span className="text-[#059669] font-semibold">Bayi Demo</span>
                <span className="text-[10px] text-muted-foreground">bayi@webplace.tr</span>
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] text-muted-foreground">Bilgileri otomatik doldurur — "Giriş Yap"a tıklayın.</p>
          </div>

          <div className="flex items-center justify-center gap-4 pt-4 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              <span>256-bit SSL</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              <span>KVKK Uyumlu</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
